import { DestroyRef, Injectable, NgZone, effect, inject, signal, untracked } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom, timeout } from 'rxjs';
import { LoginService } from '../services/login.service';
import { HelpCatalog } from './help.models';
import { loadHelpMarkdown } from './help-markdown.mjs';

@Injectable({ providedIn: 'root' })
export class HelpAccessService {
    readonly login = inject(LoginService);
    // Assets do próprio frontend, sem Bearer ou interceptor operacional.
    private http = new HttpClient(inject(HttpBackend));
    private zone = inject(NgZone);
    readonly authorized = signal(false);
    readonly failure = signal<'session' | 'unavailable' | null>(null);
    readonly denied = new Subject<void>();
    private token = '';
    private generation = 0;

    constructor() {
        const changed = () => this.zone.run(() => {
            if (this.token && (this.token !== this.login.getToken() || !this.login.isAutenticado())) this.clear();
        });
        const storage = (e: StorageEvent) => { if (!e.key || e.key === 'auth-session') changed(); };
        const restore = (e: PageTransitionEvent) => { if (e.persisted) { this.clear(); location.reload(); } };
        const focus = () => { changed(); if (this.authorized()) void this.ensure(); };
        const offline = () => this.zone.run(() => this.clear('unavailable'));
        window.addEventListener('storage', storage);
        window.addEventListener('focus', focus);
        window.addEventListener('pageshow', restore);
        window.addEventListener('pagehide', changed);
        window.addEventListener('offline', offline);
        effect(() => { this.login.session(); untracked(changed); });
        // Mesma sessão do portal em todas as janelas, inclusive expiração JWT.
        let timer: ReturnType<typeof setInterval>;
        this.zone.runOutsideAngular(() => timer = setInterval(() => this.zone.run(() => {
            changed();
        }), 1000));
        inject(DestroyRef).onDestroy(() => {
            clearInterval(timer);
            window.removeEventListener('storage', storage);
            window.removeEventListener('focus', focus);
            window.removeEventListener('pageshow', restore);
            window.removeEventListener('pagehide', changed);
            window.removeEventListener('offline', offline);
        });
    }

    clear(reason: 'session' | 'unavailable' = 'session') {
        this.generation++;
        this.token = '';
        this.authorized.set(false);
        this.failure.set(reason);
        this.denied.next();
    }

    async ensure(): Promise<boolean> {
        if (!this.login.isAutenticado()) { this.clear(); return false; }
        const token = this.login.getToken();
        if (this.token && this.token !== token) this.clear();
        this.token = token;
        this.authorized.set(true);
        this.failure.set(null);
        return true;
    }

    async content(): Promise<HelpCatalog | null> {
        if (!await this.ensure()) return null;
        const token = this.token, generation = this.generation;
        try {
            const value = await loadHelpMarkdown(file => {
                if (generation !== this.generation || token !== this.login.getToken() || !this.login.isAutenticado()) throw new Error('Sessão encerrada.');
                const url = new URL('assets/help/' + file, document.baseURI).href;
                return firstValueFrom(this.http.get(url, { responseType: 'text', headers: { 'Cache-Control': 'no-cache' } }).pipe(timeout(10000)));
            });
            if (generation !== this.generation || token !== this.login.getToken() || !this.login.isAutenticado()) return null;
            return value;
        } catch { if (generation === this.generation) this.clear(this.login.isAutenticado() ? 'unavailable' : 'session'); return null; }
    }
}
